import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputLabel,
} from "@mui/material";
// import Cookies from "js-cookie";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const ARMHSCrisisPlanPreview = ({ patientFormId, data }) => {
  const [noteData, setNoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [practiceLogo, setPracticeLogo] = useState(null);
  const [practiceName, setPracticeName] = useState("");

  // Signature preview link (patient image only)
  const [patientSigLink, setPatientSigLink] = useState("");

  const resolvedPatientFormId = useMemo(
    () =>
      patientFormId ??
      data?.patientFormId ??
      data?.patientformID ??
      data?.id ??
      data?.Id ??
      null,
    [patientFormId, data]
  );

  useEffect(() => {
    try {
      const logo =
        typeof window !== "undefined"
          ? localStorage.getItem("practiceLogo1")
          : null;
      if (logo) setPracticeLogo(logo);
    } catch { }
  }, []);

  // Fetch Crisis Plan and Base Form, then merge like your Collateral preview
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      if (!resolvedPatientFormId) {
        if (active) {
          setError("Missing patientFormId");
          setLoading(false);
        }
        return;
      }
      if (!API_BASE_URL) {
        if (active) {
          setError(
            "API base URL is not set. Define NEXT_PUBLIC_API_BASE_URL or REACT_APP_API_URL and restart."
          );
          setLoading(false);
        }
        return;
      }

      const token = localStorage.getItem('neuro_patientportal_auth_token')
      if (!token) {
        if (active) {
          setError("Not authenticated (missing token)");
          setLoading(false);
        }
        return;
      }

      try {
        if (active) {
          setLoading(true);
          setError("");
        }

        const crisisUrl = `${API_BASE_URL}/Rehabilitative/getCrisisPlanById?patientformID=${encodeURIComponent(
          resolvedPatientFormId
        )}`;
        const formUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${encodeURIComponent(
          resolvedPatientFormId
        )}`;

        const [crisisRes, formRes] = await Promise.all([
          fetch(crisisUrl, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(formUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        let crisis = {};
        if (crisisRes.ok && crisisRes.status !== 204) {
          crisis = await crisisRes.json();
        } else if (!crisisRes.ok && crisisRes.status !== 404) {
          const text = await crisisRes.text().catch(() => "");
          throw new Error(text || "Failed to fetch Crisis Plan");
        }

        let form = {};
        if (formRes.ok && formRes.status !== 204) {
          form = await formRes.json();
        }

        // Merge so base form fills header fields reliably
        const merged = { ...crisis, ...form };

        if (active) {
          setNoteData(merged || {});
          setPracticeName(merged?.practiceName || merged?.particeName || "");
        }
      } catch (err) {
        if (active) {
          setError(err?.message || "Failed to fetch Crisis Plan");
          setNoteData(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [resolvedPatientFormId]);

  // Fetch only patient signature image (staff/supervisor are text-only)
  useEffect(() => {
    const token = localStorage.getItem('neuro_patientportal_auth_token')
    if (!token || !API_BASE_URL || !noteData) return;

    const getPreviewLink = async (path) => {
      try {
        if (!path) return "";
        const res = await fetch(
          `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
            path
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return "";

        const ct = res.headers.get("content-type") || "";
        // If API returns a direct URL as text/plain
        if (ct.includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }
        // Otherwise assume binary and create object URL
        const blob = await res.blob();
        if (blob && blob.size > 0) {
          return URL.createObjectURL(blob);
        }
        return "";
      } catch {
        return "";
      }
    };

    (async () => {
      const patientPath =
        noteData.patientSignatureURL ||
        noteData.clientsURL ||
        noteData.clientSignatureURL;

      const p = await getPreviewLink(patientPath);
      setPatientSigLink(p);
    })();

    return () => {
      try {
        patientSigLink &&
          patientSigLink.startsWith("blob:") &&
          URL.revokeObjectURL(patientSigLink);
      } catch { }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData]);

  // Format MM-DD-YYYY
  const formatMMDDYYYY = (input) => {
    if (!input) return "";
    if (typeof input === "string") {
      const s = input.trim();
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
      if (iso) {
        const [, y, mo, da] = iso;
        const dt = new Date(+y, +mo - 1, +da);
        return Number.isNaN(dt.getTime()) ? "" : `${mo}-${da}-${y}`;
      }
      const mdy = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (mdy) {
        const [, mo, da, y] = mdy;
        const dt = new Date(+y, +mo - 1, +da);
        const mm = String(mo).padStart(2, "0");
        const dd = String(da).padStart(2, "0");
        return Number.isNaN(dt.getTime()) ? "" : `${mm}-${dd}-${y}`;
      }
    }
    const dt = new Date(input);
    if (Number.isNaN(dt.getTime())) return "";
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const firstOf = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ||
    "";

  const note = noteData || {};
  const dobRaw = note.dob || note.dOB || note.DOB || null;
  const fileNo = note.fileNo || note.fileno || note.accountNo || "";
  const phoneVal = note.phoneno || note.phoneNumber || note.phone || "";

  // Manual signature names and dates (fallbacks)
  const patientSignedName = firstOf(
    note.patientSignatureName,
    note.clientSignatureName,
    note.patientSignedName,
    note.clientSignedName
  );
  const patientSignedDate = firstOf(
    note.patientSignatureDATE,
    note.clientSignatureDate,
    note.PatientSignedDate
  );

  const staffSignedName = firstOf(
    note.massagestaffSignature, // used in your other previews
    note.staffSignatureName,
    note.staff_signature_name,
    note.staffName
  );
  const staffSignedDate = firstOf(
    note.staffSignatureDATE,
    note.staffsignatureDATE,
    note.staffSignDate
  );

  const supervisorSignedName = firstOf(
    note.massageSupervisorSignature,
    note.supervisorSignatureName,
    note.supervisorName
  );
  const supervisorSignedDate = firstOf(
    note.supervisorSignatureDATE,
    note.SupervisorDate
  );

  const directorSignedName = firstOf(
    note.messagetreatmentdirector,
    note.treatmentDirectorName,
    note.directorSignatureName
  );
  const directorSignedDate = firstOf(
    note.directorDate,
    note.treatmentdirectorDate
  );

  // Generic signature row: image (if provided) else text+date
  const renderSignature = (label, imgLink, typedName, dateVal) => {
    const dateText = formatMMDDYYYY(dateVal);
    return (
      <tr>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }}>
          <Typography>{label}</Typography>
        </td>
        <td style={{ padding: "8px", width: "50%", verticalAlign: "top" }}>
          {imgLink ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={imgLink}
                alt={`${label} Image`}
                style={{
                  display: "block",
                  width: "200px",
                  height: "80px",
                  border: "2px solid #000",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                  objectFit: "contain",
                }}
              />
              {dateText ? (
                <p style={{ fontWeight: 700, fontSize: "16px" }}>
                  Signed on {dateText}
                </p>
              ) : null}
            </div>
          ) : typedName || dateText ? (
            // Manual signature fallback: show signer name and date
            <div>
              <Typography sx={{ fontWeight: "bold" }}>
                {typedName || "Digitally Signed"}
              </Typography>
              {dateText ? (
                <Typography variant="body2" color="text.secondary">
                  Signed on {dateText}
                </Typography>
              ) : null}
            </div>
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </td>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }} />
      </tr>
    );
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography align="center" color="error" sx={{ mt: 4 }}>
        {error}
      </Typography>
    );

  return (
    <Paper sx={{ p: 0, mb: 6, boxShadow: 3, borderRadius: 3, bgcolor: "#fff" }}>
      {/* Collateral-style sticky header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px", // align with modal scroll padding (p:2 = 16px)
          zIndex: (theme) => theme.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ position: "absolute", top: 16, left: 16 }}
        >
          <strong>DOS:</strong>{" "}
          {formatMMDDYYYY(note.assessmentDate || note.sessionDate) || "—"}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {note.customFormTitle || data?.formTitle || "ARMHS Crisis Plan"}
        </Typography>

        {practiceLogo && (
          <Box
            component="img"
            src={practiceLogo}
            alt="Practice Logo"
            sx={{ width: 65, height: 65, borderRadius: 1, mb: 2, mx: "auto" }}
          />
        )}
        {/* <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
          {practiceName || ""}
        </Typography> */}
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip (like Collateral) */}
      <Box sx={{ px: 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {fileNo || "—"}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client:</strong>{" "}
              {note.patientName || note.PatientNameclient || note.clientName || "—"}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {formatMMDDYYYY(dobRaw) || "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Body (unchanged) */}
      <Box sx={{ px: 4, pb: 4 }}>
        {/* Title + Assessment Date */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Title"
              value={note.customFormTitle || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Assessment Date"
              value={formatMMDDYYYY(note.assessmentDate)}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        {/* Flags */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={!!note.isPrinted} disabled size="small" />}
              label="Printed and Filed"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={!!note.isChecked} disabled size="small" />}
              label="Inactive"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={!!note.isAuditReady} disabled size="small" />}
              label="Audit Ready"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={!!note.formlock} disabled size="small" />}
              label="Lock"
            />
          </Grid>
        </Grid>

        {/* Main heading */}
        <Typography variant="h5" sx={{ color: "black", fontWeight: "bold" }}>
          Adult Rehabilitative Mental Health Services Team Crisis Plan
        </Typography>

        {/* Client Info */}
        <Box sx={{ mt: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>Client Name:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <Typography sx={{ color: "black", fontWeight: "bold" }}>
                    {note.patientName ||
                      note.PatientNameclient ||
                      note.clientName ||
                      ""}
                  </Typography>
                </td>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>File #:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <Typography sx={{ color: "black", fontWeight: "bold" }}>
                    {fileNo || ""}
                  </Typography>
                </td>
              </tr>

              <tr>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>DOB:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={formatMMDDYYYY(dobRaw)}
                    InputProps={{ readOnly: true }}
                  />
                </td>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>Date of Plan:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={formatMMDDYYYY(note.dateOfplan || note.dateOfPlan)}
                    InputProps={{ readOnly: true }}
                  />
                </td>
              </tr>

              <tr>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>Address:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={note.address || ""}
                    InputProps={{ readOnly: true }}
                  />
                </td>
                <td style={{ padding: "8px", width: "16.67%" }}>
                  <InputLabel>Phone:</InputLabel>
                </td>
                <td style={{ padding: "8px", width: "33.33%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={phoneVal}
                    InputProps={{ readOnly: true }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        {/* Directions */}
        <Box sx={{ mt: 2 }}>
          <InputLabel>Directions</InputLabel>
          <TextField
            value={note.directions || ""}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
          />
        </Box>

        {/* Diagnoses */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <InputLabel>Primary Diagnosis</InputLabel>
            <TextField
              value={note.primaryDiagnosis || ""}
              fullWidth
              multiline
              minRows={2}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel>Medical Diagnosis</InputLabel>
            <TextField
              value={note.medicalDiagnosis || ""}
              fullWidth
              multiline
              minRows={2}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Current Medications */}
        <Typography variant="h5" sx={{ color: "black", fontWeight: "bold" }}>
          Current Medications
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "black", mt: 1 }}>
          (See list below or attached list.)
        </Typography>

        {note.medications?.length > 0 ? (
          note.medications.map((med, index) => (
            <Paper
              key={index}
              elevation={3}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #ddd",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                mt: 1.5,
                mb: 1.5,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "8px", width: "25%" }}>
                      <InputLabel>Medication</InputLabel>
                      <TextField
                        fullWidth
                        size="small"
                        value={med.medicationName || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </td>
                    <td style={{ padding: "8px", width: "25%" }}>
                      <InputLabel>Dose</InputLabel>
                      <TextField
                        fullWidth
                        size="small"
                        value={med.medicationsDose || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </td>
                    <td style={{ padding: "8px", width: "25%" }}>
                      <InputLabel>Prescribed By</InputLabel>
                      <TextField
                        fullWidth
                        size="small"
                        value={med.prescribedBy || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </td>
                    <td style={{ padding: "8px", width: "25%" }}>
                      <InputLabel>Purpose</InputLabel>
                      <TextField
                        fullWidth
                        size="small"
                        value={med.purpose || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Paper>
          ))
        ) : (
          <Typography variant="body2">No Medications Defined</Typography>
        )}

        {/* Allergies and Pharmacy */}
        <Box sx={{ mt: 2 }}>
          <InputLabel>Allergies</InputLabel>
          <TextField
            value={note.allergies || ""}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel>Pharmacy</InputLabel>
              <TextField
                value={note.pharmacy || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel>Phone</InputLabel>
              <TextField
                value={note.phone || note.phoneNumber || ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Persons to Contact in Case of Emergency */}
        <Typography variant="h6" gutterBottom>
          Persons to Contact in Case of Emergency
        </Typography>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
          <tbody>
            <tr>
              <td style={{ padding: "8px", width: "33.33%" }}></td>
              <td style={{ padding: "8px", width: "33.33%" }}>
                <Typography style={{ fontWeight: "bold" }}>Name</Typography>
              </td>
              <td style={{ padding: "8px", width: "33.33%" }}>
                <Typography>Phone Number</Typography>
              </td>
            </tr>

            {[
              { label: "Team", name: note.teamName, phone: note.teamPhoneNumber },
              { label: "Family/Significant other", name: note.familyName, phone: note.familyPhoneNumber },
              { label: "Psychiatrist", name: note.psychiatristName, phone: note.psychiatristPhoneNumber },
              { label: "Primary Care Physician", name: note.carePhysicianName, phone: note.carePhysicianPhoneNumber },
              { label: "Therapist", name: note.therapistName, phone: note.therapistPhoneNumber },
              { label: "Employer", name: note.employerName, phone: note.employerPhoneNumber },
              { label: "Other", name: note.otherName, phone: note.otherPhoneNumber },
            ].map((row, idx) => (
              <tr key={idx}>
                <td style={{ padding: "8px" }}>{row.label}</td>
                <td style={{ padding: "8px" }}>
                  <TextField size="small" fullWidth value={row.name || ""} InputProps={{ readOnly: true }} />
                </td>
                <td style={{ padding: "8px" }}>
                  <TextField size="small" fullWidth value={row.phone || ""} InputProps={{ readOnly: true }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Divider sx={{ my: 3 }} />

        {/* Advance Directives + Insurance */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "8px", width: "50%", verticalAlign: "top" }}>
                <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                  <input
                    type="checkbox"
                    checked={!!note.advanceDirectives}
                    readOnly
                    style={{ marginLeft: "10px", transform: "scale(1.0)", marginRight: "5px" }}
                  />
                  <label style={{ fontSize: "14px", color: "black", fontWeight: "bold" }}>
                    Advance Directives
                  </label>
                </div>
                <InputLabel style={{ marginTop: "8px" }}>Directives</InputLabel>
                <TextField value={note.directives || ""} fullWidth InputProps={{ readOnly: true }} />
              </td>
              <td style={{ padding: "8px", width: "50%", verticalAlign: "bottom" }}>
                <InputLabel>Insurance</InputLabel>
                <TextField value={note.insurance || ""} fullWidth InputProps={{ readOnly: true }} />
              </td>
            </tr>
          </tbody>
        </table>

        <Divider sx={{ my: 3 }} />

        {/* Client Section */}
        <Typography variant="h6" gutterBottom>
          Client Section
        </Typography>

        <Box sx={{ mt: 1 }}>
          <InputLabel>Previous suicide attempts and methods</InputLabel>
          <TextField value={note.previoussuicide || ""} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />

          <InputLabel>Crisis Precipitators - What makes me feel in crisis?</InputLabel>
          <TextField value={note.crisisPrecipitators || ""} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />

          <InputLabel>Coping skills I can use to prevent, cope with, and recover from a crisis:</InputLabel>
          <TextField value={note.copingskills || ""} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />

          <InputLabel>The team can do the following to help me in crisis:</InputLabel>
          <TextField value={note.teamHelp || ""} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />

          <InputLabel>Emergency Dependent Care (child, pet, etc.) Arrangements:</InputLabel>
          <TextField value={note.emergencyDependent || ""} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Crisis Coping Steps and Options */}
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
          CRISIS COPING STEPS AND OPTIONS
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Typography>1. Use my crisis coping plan</Typography>
          <Typography>2. Call TEAM at {note.callTEAM || ""}</Typography>
          <Typography>3. Go to the Emergency Room</Typography>
          <Typography>4. Call 911</Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Signatures */}
        <Typography variant="h6" gutterBottom>
          Signatures
        </Typography>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {/* Patient Signature: image if available; else typed name/date */}
            {renderSignature("Patient Signature:", patientSigLink, patientSignedName, patientSignedDate)}

            {/* Staff Signature: text + date (like Director) */}
            {renderSignature("Staff Signature:", "", staffSignedName, staffSignedDate)}

            {/* Supervisor Signature: text + date (like Director) */}
            {renderSignature("Supervisor Signature:", "", supervisorSignedName, supervisorSignedDate)}

            {/* ARMHS Treatment Director: text + date */}
            {renderSignature("ARMHS Treatment Director", "", directorSignedName, directorSignedDate)}
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default ARMHSCrisisPlanPreview;
